import { Auth } from "aws-amplify";
import { User, UserRole } from "../types";
import { config } from "../config";

// Configure Amplify with Cognito information
Auth.configure({
  region: config.region,
  userPoolId: config.userPoolId,
  userPoolWebClientId: config.userPoolClientId,
});

// Login user
export const loginUser = async (
  email: string,
  password: string
): Promise<User> => {
  try {
    const cognitoUser = await Auth.signIn(email, password);

    // Get user attributes
    const attributes = cognitoUser.attributes || {};

    // Auto-generate familyId if user doesn't have one
    let familyId = attributes["custom:familyId"] || "";
    if (!familyId) {
      // Generate a family ID based on the user's ID for consistency
      familyId = `family-${cognitoUser.username}`;

      // Update Cognito attributes with the new familyId
      try {
        await Auth.updateUserAttributes(cognitoUser, {
          "custom:familyId": familyId,
        });
        console.log(
          `Auto-generated familyId: ${familyId} for user: ${cognitoUser.username}`
        );
      } catch (updateError) {
        console.error("Failed to update familyId in Cognito:", updateError);
        // Continue with the generated familyId even if update fails
      }
    }

    return {
      userId: cognitoUser.username,
      email: attributes.email || email,
      familyId: familyId,
      role: (attributes["custom:role"] as UserRole) || UserRole.CHILD,
      firstName: attributes.given_name || "",
      lastName: attributes.family_name || "",
      avatar: attributes["custom:avatar"] || undefined,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      token: cognitoUser.signInUserSession.idToken.jwtToken,
    };
  } catch (error) {
    console.error("Login error:", error);
    throw error;
  }
};

// Register user
export const registerUser = async (userData: any): Promise<User> => {
  try {
    // Register user in Cognito
    const result = await Auth.signUp({
      username: userData.email,
      password: userData.password,
      attributes: {
        email: userData.email,
        given_name: userData.firstName,
        family_name: userData.lastName,
        "custom:role": userData.role,
        "custom:familyId": userData.familyId || "", // Will be updated after family creation
      },
    });

    // Return a minimal user object indicating registration was successful
    // The user will need to confirm their email before they can actually log in
    return {
      userId: result.userSub,
      email: userData.email,
      familyId: userData.familyId || "",
      role: userData.role,
      firstName: userData.firstName,
      lastName: userData.lastName,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      token: "", // No token until confirmed and logged in
    };
  } catch (error) {
    console.error("Registration error:", error);
    throw error;
  }
};

// Confirm user registration
export const confirmUser = async (
  email: string,
  code: string
): Promise<void> => {
  try {
    await Auth.confirmSignUp(email, code);
  } catch (error) {
    console.error("Confirmation error:", error);
    throw error;
  }
};

// Resend confirmation code
export const resendConfirmationCode = async (email: string): Promise<void> => {
  try {
    await Auth.resendSignUp(email);
  } catch (error) {
    console.error("Resend confirmation error:", error);
    throw error;
  }
};

// Logout user
export const logoutUser = async (): Promise<void> => {
  try {
    await Auth.signOut();
  } catch (error) {
    console.error("Logout error:", error);
    throw error;
  }
};

// Get current authenticated user
export const getCurrentUser = async (): Promise<User | null> => {
  try {
    const cognitoUser = await Auth.currentAuthenticatedUser();
    const attributes = cognitoUser.attributes || {};

    // Auto-generate familyId if user doesn't have one
    let familyId = attributes["custom:familyId"] || "";
    if (!familyId) {
      // Generate a family ID based on the user's ID for consistency
      familyId = `family-${cognitoUser.username}`;

      // Update Cognito attributes with the new familyId
      try {
        await Auth.updateUserAttributes(cognitoUser, {
          "custom:familyId": familyId,
        });
        console.log(
          `Auto-generated familyId: ${familyId} for user: ${cognitoUser.username}`
        );
      } catch (updateError) {
        console.error("Failed to update familyId in Cognito:", updateError);
        // Continue with the generated familyId even if update fails
      }
    }

    return {
      userId: cognitoUser.username,
      email: attributes.email || "",
      familyId: familyId,
      role: (attributes["custom:role"] as UserRole) || UserRole.CHILD,
      firstName: attributes.given_name || "",
      lastName: attributes.family_name || "",
      avatar: attributes["custom:avatar"] || undefined,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      token: cognitoUser.signInUserSession.idToken.jwtToken,
    };
  } catch (error) {
    // User is not authenticated
    return null;
  }
};

// Request password reset
export const forgotPassword = async (email: string): Promise<void> => {
  try {
    await Auth.forgotPassword(email);
    // Store email for later use in resetPassword and verifyResetCode
    localStorage.setItem("resetEmail", email);
  } catch (error) {
    console.error("Forgot password error:", error);
    throw error;
  }
};

// Reset password with code
export const resetPassword = async (
  code: string,
  newPassword: string
): Promise<void> => {
  try {
    // We need to get the email from localStorage or state
    // This is a limitation of the current design - we need the email for AWS Cognito
    const email = localStorage.getItem("resetEmail");
    if (!email) {
      throw new Error(
        "Email not found. Please restart the password reset process."
      );
    }
    await Auth.forgotPasswordSubmit(email, code, newPassword);
    localStorage.removeItem("resetEmail");
  } catch (error) {
    console.error("Reset password error:", error);
    throw error;
  }
};

// Verify reset code
export const verifyResetCode = async (code: string): Promise<boolean> => {
  try {
    // We need to get the email from localStorage or state
    const email = localStorage.getItem("resetEmail");
    if (!email) {
      throw new Error(
        "Email not found. Please restart the password reset process."
      );
    }

    // We can try to submit with a dummy password to validate the code
    // If it fails due to password requirements, the code is valid
    // If it fails due to invalid code, the code is invalid
    await Auth.forgotPasswordSubmit(email, code, "DummyPassword123!");
    return true;
  } catch (error: any) {
    if (
      error.code === "InvalidPasswordException" ||
      error.code === "NotAuthorizedException"
    ) {
      return true; // Code is valid, just password format issue
    }
    console.error("Verify reset code error:", error);
    return false;
  }
};

// Refresh current user session to get updated attributes from Cognito
export const refreshUserSession = async (): Promise<User | null> => {
  try {
    // Force refresh of the current user from Cognito
    const cognitoUser = await Auth.currentAuthenticatedUser({
      bypassCache: true,
    });
    const attributes = cognitoUser.attributes || {};

    // Auto-generate familyId if user doesn't have one
    let familyId = attributes["custom:familyId"] || "";
    if (!familyId) {
      // Generate a family ID based on the user's ID for consistency
      familyId = `family-${cognitoUser.username}`;

      // Update Cognito attributes with the new familyId
      try {
        await Auth.updateUserAttributes(cognitoUser, {
          "custom:familyId": familyId,
        });
        console.log(
          `Auto-generated familyId: ${familyId} for user: ${cognitoUser.username}`
        );
      } catch (updateError) {
        console.error("Failed to update familyId in Cognito:", updateError);
        // Continue with the generated familyId even if update fails
      }
    }

    return {
      userId: cognitoUser.username,
      email: attributes.email || "",
      familyId: familyId,
      role: (attributes["custom:role"] as UserRole) || UserRole.CHILD,
      firstName: attributes.given_name || "",
      lastName: attributes.family_name || "",
      avatar: attributes["custom:avatar"] || undefined,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      token: cognitoUser.signInUserSession.idToken.jwtToken,
    };
  } catch (error) {
    console.error("Error refreshing user session:", error);
    // User is not authenticated or error occurred
    return null;
  }
};
